import React, { Component, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import _ from 'lodash';
import { AppContainer } from '@prcosystems/react-common';
import { useAuth0 } from '@auth0/auth0-react';
import { connect } from 'react-redux';
import { onload } from 'redux-load-api';
import Helmet from 'react-helmet';
import cn from 'classnames';
import url from 'url';
import { Alert, Button, ProgressBar } from 'react-bootstrap';
import Info from '../components/Info';
import Notifs from '../components/Notifs';
import UserSnap from '../components/UserSnap';
import { AuthContext } from '../components/Auth';
import { RequireAccountOnce } from '../components/RequireAccountOnce';
import * as storage from '../utils/storage';
import InfoMessagesModal from '../components/InfoMessagesModal';
import './App.css';

class AppErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorMessage: null };
  }

  componentDidCatch(error, info) {
    console.error(error);
    console.log(info);
    return { hasError: true, errorMessage: error.toString() };
  }

  dismissError = () => this.setState({ hasError: false, errorMessage: null });

  render() {
    return <App {...this.props} {...this.state} dismissError={this.dismissError} />;
  }
}

export function App(props) {
  const { info, loading, children, hasError, errorMessage, dismissError } = props;
  const { user } = useAuth0();
  const { logout } = useContext(AuthContext);
  const [showInfoModal, setShowInfoModal] = useState(false);

  return (
    <AppContainer
      showBrowserWarning={true}
      showInfoIcon={true}
      onInfoIconCLicked={() => setShowInfoModal(true)}
      onLogoutClick={logout}
      token={user}
      apiUrl="/"
    >
      <InfoMessagesModal onHide={() => setShowInfoModal(false)} show={showInfoModal} />
      <Helmet title="PRco Payment Gateway" />
      <UserSnap />
      <RequireAccountOnce />

      <div className="app-content">
        <div className={cn('container-fluid', 'page-container')}>
          {info && info.message && (
            <div className="container">
              <Info className="info" />
            </div>
          )}
          {loading ? <ProgressBar now={100} active={true} /> : null}
          {hasError ? (
            <Alert bsStyle="danger" onDismiss={dismissError} style={{ width: 500, margin: 'auto' }}>
              <h4>The application has experienced an error</h4>
              {errorMessage}
              <p>You may need to refresh the application.</p>
              <Button bsStyle="danger" onClick={() => document.location.reload()}>
                Take action
              </Button>
              <span> or </span>
              <Button onClick={dismissError}>Dismiss</Button>
            </Alert>
          ) : (
            children
          )}
          <div className="container">
            <Notifs className="notifications" namespace="global" />
          </div>
        </div>
      </div>
    </AppContainer>
  );
}

App.propTypes = {
  loading: PropTypes.bool,
  children: PropTypes.object.isRequired,
  info: PropTypes.object.isRequired,
  hasError: PropTypes.bool,
  errorMessage: PropTypes.string,
  dismissError: PropTypes.func.isRequired,
};

App.contextTypes = {
  store: PropTypes.object.isRequired,
};

App.defaultProps = {
  user: null,
  loading: false,
  hasError: false,
  errorMessage: null,
};

export default onload(() => {
  const { query } = url.parse(window.location.href, true);
  // NOTE - We store payment queries from the prco statements dashboard
  if (_.get(query, 'payment')) {
    storage.put('paymentQuery', {
      payment: query.payment,
      contractNumbers: _.chain(query.contractNumbers).split(',').map(_.trim).value(),
      amounts: _.chain(query.amounts).split(',').map(_.trim).value(),
      comments: query.comments,
      customComment: query.customComment,
      nsfComment: query.nsfComment,
    });
  }
})(
  connect((state) => ({
    info: state.info,
    loading: state.page.loading,
  }))(AppErrorBoundary),
);
